import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getSupabaseClient } from '../lib/supabase';

type Props = {};

type StepStatus = 'done' | 'todo';

interface Step {
  label: string;
  desc: string;
  status: StepStatus;
  path?: string;
  optional?: boolean;
}

export default function CompleteProfile({}: Props) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<Step[]>([
    { label: 'Basic Information', desc: 'Name, email, password', status: 'done' },
    { label: 'Professional Experience', desc: 'Overview of your experience and history', status: 'done' },
    {
      label: 'Enhanced Guides Profile',
      desc: 'Complete profile mean better matches and success',
      status: 'todo',
      path: '/onboarding/complete-profile/enhanced-profile',
    },
    {
      label: 'Professional References',
      desc: 'From an industry professional and a client',
      status: 'todo',
      path: '/onboarding/complete-profile/references',
    },
    {
      label: 'Set Your Schedule',
      desc: 'Required for activation — seekers need your availability to book consultation calls',
      status: 'todo',
      path: '/onboarding/complete-profile/schedule',
    },
    {
      label: 'Your Standard Packages',
      desc: 'Helps us match with the right seekers',
      status: 'todo',
      path: '/onboarding/complete-profile/packages',
    },
    {
      label: 'Criminal Background Check',
      desc: 'Upload background check documents if available',
      status: 'todo',
      path: '/onboarding/complete-profile/background-check',
      optional: true,
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const sb = getSupabaseClient();

  useEffect(() => {
    async function checkSectionCompletion() {
      const {
        data: { user },
      } = await sb.auth.getUser();

      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check enhanced profile (stored in guides_enhanced_profile table)
        const { data: profile } = await sb
          .from('guides_enhanced_profile')
          .select('*')
          .eq('guide_id', user.id)
          .single();

        // Only require 'about' field - image_url is optional
        const enhancedProfileDone = profile && profile.about;

        // Check references
        const { data: references } = await sb
          .from('guide_references')
          .select('*')
          .eq('user_id', user.id);
        
        const referencesDone = references && references.length >= 1;

        // Check background check
        const { data: backgroundCheck } = await sb
          .from('guide_background_checks')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        const backgroundCheckDone = !!backgroundCheck;

        // Check working hours / schedule
        const { data: workingHours } = await sb
          .from('guide_working_hours')
          .select('*')
          .eq('guide_id', user.id);

        const scheduleDone = workingHours && workingHours.some((h: any) => h.is_active);

        // Check packages
        const { data: packages } = await sb
          .from('guide_packages')
          .select('*')
          .eq('guide_id', user.id);

        const packagesDone = packages && packages.length > 0;

        // Update steps with actual completion status
        setSteps((prevSteps) =>
          prevSteps.map((step, index) => {
            if (index === 2) {
              return { ...step, status: enhancedProfileDone ? 'done' : 'todo' };
            }
            if (index === 3) {
              return { ...step, status: referencesDone ? 'done' : 'todo' };
            }
            if (index === 4) {
              return { ...step, status: scheduleDone ? 'done' : 'todo' };
            }
            if (index === 5) {
              return { ...step, status: packagesDone ? 'done' : 'todo' };
            }
            if (index === 6) {
              return { ...step, status: backgroundCheckDone ? 'done' : 'todo' };
            }
            return step;
          })
        );

      } catch (error) {
        console.error('Error checking section completion:', error);
      } finally {
        setLoading(false);
      }
    }

    checkSectionCompletion();
  }, []);

  const requiredSteps = steps.filter(step => !step.optional);
  const optionalSteps = steps.filter(step => step.optional);
  const completedRequiredCount = requiredSteps.filter(step => step.status === 'done').length;
  const completedOptionalCount = optionalSteps.filter(step => step.status === 'done').length;
  const progressPercentage = requiredSteps.length
    ? (completedRequiredCount / requiredSteps.length) * 100
    : 0;
  const allRequiredComplete = completedRequiredCount === requiredSteps.length;

  const handleSubmitForActivation = async () => {
    if (!allRequiredComplete) return;

    setSubmitting(true);
    try {
      const {
        data: { session },
      } = await sb.auth.getSession();

      if (!session) {
        alert('Not logged in');
        return;
      }

      const env = (window as any).__ENV__;
      const res = await fetch(`${env.SUPABASE_URL}/functions/v1/submit-for-activation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const result = await res.json();

      if (!res.ok) {
        console.error('Error submitting for activation:', result);
        alert('Failed to submit for activation. Please try again.');
      } else {
        // Navigate to onboarding to show the new status
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error submitting for activation:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fffcef] px-4">
        <div className="text-center text-[#304031]">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#304031]/20 border-t-[#304031]" />
          <p className="text-[#304031]/70">Checking your profile status…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffcef] px-4 py-10">
      <div className="mx-auto max-w-3xl text-[#304031]">
      {/* Icon */}
      <div className="mb-8 flex justify-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#304031]/10">
          <CheckCircleIcon className="h-10 w-10 text-[#304031]" />
        </div>
      </div>

      <h1 className="mb-3 text-center font-recoleta text-[42px] leading-[44px] font-medium text-[#304031]">Complete Your Profile</h1>
      <p className="mb-12 text-center text-lg text-[#304031]/70">
        Your guide application is tentatively approved, complete your profile and we'll get you up and running quickly.
      </p>

      {/* Profile Completion Card */}
      <div className="mb-10 rounded-2xl border-2 border-[#a0978a] bg-[#fffcef] p-8 text-left shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <p className="font-semibold text-lg text-[#304031]">Profile Completion</p>
          <div className="text-right text-sm text-[#304031]/70">
            <p>{completedRequiredCount} of {requiredSteps.length} required completed</p>
            {optionalSteps.length > 0 && (
              <p className="text-xs text-[#304031]/60">{completedOptionalCount} of {optionalSteps.length} optional completed</p>
            )}
          </div>
        </div>
        {/* progress bar */}
        <div className="mb-6 h-2 overflow-hidden rounded-full bg-[#E8DFBD]">
          <div className="h-2 rounded-full bg-[#D29B04]" style={{ width: `${progressPercentage}%` }} />
        </div>

        {/* steps */}
        <ul className="space-y-3">
          {steps.map((item, i) => (
            <li key={i} className="flex items-center justify-between rounded-xl border-2 border-[#a0978a] bg-[#fffcef] px-5 py-4">
              <div className="flex items-start gap-3">
                {item.status === 'done' ? (
                  <CheckCircleIcon className="mt-0.5 h-6 w-6 text-[#26A885]" />
                ) : (
                  <ExclamationCircleIcon className="mt-0.5 h-6 w-6 text-[#C47C16]" />
                )}
                <div>
                  <p className="font-medium text-[#304031]">{item.label}</p>
                  <p className="text-sm text-[#304031]/70">
                    {item.desc}
                    {item.optional && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-[#FFF3D9] px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#C47C16]">Optional</span>
                    )}
                  </p>
                </div>
              </div>
              {item.status === 'done' ? (
                <span className="text-sm font-semibold text-[#26A885]">Complete</span>
              ) : (
                <button
                  onClick={() => navigate(item.path!)}
                  className="rounded-full border-2 border-[#304031] px-4 py-2 text-sm font-semibold text-[#304031] transition hover:bg-[#304031] hover:text-[#fef6d6]"
                >
                  Complete Now
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* What happens next */}
      <div className="mb-10 rounded-2xl border-2 border-[#a0978a] bg-[#fffcef] p-8 text-left shadow-sm">
        <h1 className="mb-6 text-lg font-semibold text-[#304031]">What happens next?</h1>

        <div className="space-y-5">
          {[
            {
              num: '1',
              title: 'Complete your profile',
              desc: 'Fill in all required sections to help us verify your expertise',
            },
            {
              num: '2',
              title: 'Review process (2–3 business days)',
              desc: 'Our team will verify your information and qualifications',
            },
            {
              num: '3',
              title: 'Account activation',
              desc: 'Start managing seekers and building your community',
            },
          ].map((step) => (
            <div key={step.num} className="flex items-start gap-4">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-[#304031]/10 text-[#304031] text-sm font-semibold flex-shrink-0">
                {step.num}
              </div>

              <div>
                <p className="font-medium text-[#304031] mb-1">{step.title}</p>
                <p className="text-sm text-[#304031]/70">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
        <button
          onClick={handleSubmitForActivation}
          disabled={!allRequiredComplete || submitting}
          className={`inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-8 py-3 text-base font-semibold transition ${
            allRequiredComplete && !submitting
              ? 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
              : 'cursor-not-allowed bg-[#304031]/20 text-[#304031]/40'
          }`}
        >
          <UserIcon className="h-5 w-5" />
          {submitting ? 'Submitting...' : 'Submit for Activation'}
        </button>
        <a
          href="mailto:support@guidescollective.com"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[#304031] px-6 py-3 text-sm font-semibold text-[#304031] hover:bg-[#304031] hover:text-[#fef6d6] transition"
        >
          <EnvelopeIcon className="h-5 w-5" />
          Contact Support
        </a>
      </div>

      {/* FAQ / Support note */}
      <p className="text-center text-sm text-[#304031]/60">
        Questions about the approval process? Check our Guide FAQ or contact our support team.
      </p>
      </div>
    </div>
  );
}
